import { memo, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { MediaVolumeContext } from '../../context/MediaVolumeContext';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { View } from 'foundations-components/components/View';
import I18n from 'I18n';
import { MEDIA_PLAYER_COLORS } from '../../constants/Colors';
import { LOCAL_STORAGE_KEYS } from '../../../constants/LocalStorageKeys';
import { VolumeDownIcon } from 'foundations-assets/icons/components/VolumeDownIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ICON_WIDTH = 21;
const StyledContainerDiv = styled.div.withConfig({
  displayName: "VolumeControl__StyledContainerDiv"
})(["cursor:pointer;width:100%;max-width:120px;"]);
const StyledVolumeLineReference = styled.div.withConfig({
  displayName: "VolumeControl__StyledVolumeLineReference"
})(["position:relative;height:0;left:", "px;width:auto;"], ICON_WIDTH);
const VolumeControl = /*#__PURE__*/memo(props => {
  const {
    volume,
    setVolume
  } = useContext(MediaVolumeContext);
  const {
    onVolumeChangeCallback
  } = props;
  const volumeRef = useRef(null);
  useEffect(() => {
    try {
      const volumeFromLocalStorage = localStorage.getItem(LOCAL_STORAGE_KEYS.MEDIA_VOLUME);
      const maybeNumber = Number(volumeFromLocalStorage);
      if (volumeFromLocalStorage !== null && !isNaN(maybeNumber)) {
        setVolume(maybeNumber);
      }
    } catch (error) {
      // Do nothing
    }
  }, [setVolume]);
  const handleSetVolume = v => {
    setVolume(v);
    if (onVolumeChangeCallback) {
      onVolumeChangeCallback(v);
    }
  };
  const calculateVolume = x => {
    if (volumeRef.current !== null) {
      const rect = volumeRef.current.getBoundingClientRect();
      let calculatedVolume = (x - rect.x) / (rect.width - ICON_WIDTH);
      calculatedVolume = Math.min(calculatedVolume, 1);
      calculatedVolume = Math.max(calculatedVolume, 0);
      return calculatedVolume;
    }
    return volume;
  };
  const handleMouseMove = evt => {
    evt.stopPropagation();
    const vol = calculateVolume(evt.clientX);
    handleSetVolume(vol);
  };
  const handleMouseUp = evt => {
    evt.stopPropagation();
    const vol = calculateVolume(evt.clientX);
    handleSetVolume(vol);
    window.removeEventListener('mouseup', handleMouseUp);
    window.removeEventListener('mousemove', handleMouseMove);
  };
  const handleMouseDown = evt => {
    evt.stopPropagation();
    window.addEventListener('mouseup', handleMouseUp, false);
    window.addEventListener('mousemove', handleMouseMove, false);
  };
  return /*#__PURE__*/_jsxs(StyledContainerDiv, {
    onMouseDown: handleMouseDown,
    "aria-label": I18n.text('recordingReviewComponents.media.mediaPlayer.ariaLabels.volumeControl'),
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(Box, {
        shrink: 0,
        grow: 0,
        children: /*#__PURE__*/_jsx(VolumeDownIcon, {
          color: MEDIA_PLAYER_COLORS.ICON_COLOR,
          size: "200"
        })
      }), /*#__PURE__*/_jsx(Box, {
        grow: 1,
        children: /*#__PURE__*/_jsx(View, {
          id: "styledLineBackground",
          position: "relative",
          inlineSize: "100%",
          blockSize: "4px",
          backgroundColor: MEDIA_PLAYER_COLORS.DEFAULT_STATUS_FILL,
          marginBlockEnd: "100",
          marginInlineStart: "100",
          borderRadius: "100",
          children: /*#__PURE__*/_jsx(View, {
            id: "styledLineProgress",
            "data-test-id": "mediaVolume-lineProgress",
            inlineSize: `${volume * 100}%`,
            blockSize: "4px",
            backgroundColor: MEDIA_PLAYER_COLORS.ACTIVE_STATUS_FILL,
            borderRadius: "100"
          })
        })
      })]
    }), /*#__PURE__*/_jsx(StyledVolumeLineReference, {
      ref: volumeRef,
      id: "volumeLineRef"
    })]
  });
});
export default VolumeControl;